const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('.gallery__stage');
    this.slides = Array.from(this.querySelectorAll('.gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
    this.lightbox = this.querySelector('.gallery__lightbox');
    this.zoom = parseFloat(this.dataset.zoom) || 0;
    this.index = 0;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));

    this.querySelectorAll('.gallery__arrow').forEach((arrow) =>
      arrow.addEventListener('click', () => this.goTo(this.index + (arrow.dataset.dir === 'next' ? 1 : -1)))
    );

    this.stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
    });

    if (this.slides.length > 1) {
      const observer = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          }),
        { root: this.stage, threshold: 0.6 }
      );
      this.slides.forEach((slide) => observer.observe(slide));
    }

    if (this.zoom > 1 && FINE_POINTER.matches) {
      this.slides.forEach((slide) => this.attachMagnifier(slide));
    }

    if (this.lightbox) {
      this.querySelectorAll('[data-lightbox-open]').forEach((trigger) =>
        trigger.addEventListener('click', () => this.openLightbox(trigger.closest('.gallery__slide')))
      );
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox || event.target.closest('[data-lightbox-close]')) {
          this.lightbox.close();
        }
      });
      const img = this.lightbox.querySelector('img');
      img.addEventListener('click', () => img.classList.toggle('is-zoomed'));
      this.lightbox.addEventListener('close', () => {
        img.classList.remove('is-zoomed');
        document.documentElement.classList.remove('scroll-lock');
      });
    }

    if (window.MX) {
      this.unsubscribe = window.MX.subscribe('variant:change', (variant) => {
        if (variant && variant.featured_media) this.goToMediaId(variant.featured_media.id);
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.stage.scrollTo({
      left: this.slides[clamped].offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
    this.setActive(clamped);
  }

  goToMediaId(id) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
    if (index > -1) this.goTo(index);
  }

  setActive(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  attachMagnifier(slide) {
    const src = slide.dataset.zoomSrc;
    if (!src) return;

    slide.addEventListener('pointerenter', () => {
      let pane = slide.querySelector('.gallery__magnifier');
      if (!pane) {
        pane = document.createElement('div');
        pane.className = 'gallery__magnifier';
        pane.style.backgroundImage = `url("${src}")`;
        pane.style.backgroundSize = `${this.zoom * 100}%`;
        slide.append(pane);
      }
      const img = slide.querySelector('img');
      if (img) {
        const slideRect = slide.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        pane.style.top = `${imgRect.top - slideRect.top}px`;
        pane.style.left = `${imgRect.left - slideRect.left}px`;
        pane.style.width = `${imgRect.width}px`;
        pane.style.height = `${imgRect.height}px`;
      }
    });

    slide.addEventListener('pointermove', (event) => {
      const pane = slide.querySelector('.gallery__magnifier');
      if (!pane) return;
      const rect = pane.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      pane.style.backgroundPosition = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
      pane.classList.add('is-active');
    });

    slide.addEventListener('pointerleave', () => {
      const pane = slide.querySelector('.gallery__magnifier');
      if (pane) pane.classList.remove('is-active');
    });
  }

  openLightbox(slide) {
    if (!slide || !slide.dataset.zoomSrc) return;
    const img = this.lightbox.querySelector('img');
    const source = slide.querySelector('img');
    img.src = slide.dataset.zoomSrc;
    img.alt = source ? source.alt : '';
    img.hidden = false;
    document.documentElement.classList.add('scroll-lock');
    this.lightbox.showModal();
  }
}

customElements.define('product-gallery', ProductGallery);
